import { useState } from "react";
import Navigation from "@/components/Navigation";
import { Card } from "@/components/ui/card";

const Horoscope = () => {
  const [selectedSign, setSelectedSign] = useState("aries");

  return (
    <div className="min-h-screen">
      <Navigation />
      
      <div className="pt-24 pb-12 px-4">
        <div className="container mx-auto">
          <h1 className="text-5xl font-bold text-center mb-4 text-glow">
            Horóscopo de Hoy
          </h1>
          <p className="text-center text-xl text-muted-foreground mb-12">
            Descubre qué te deparan los astros para los 12 signos zodiacales
          </p>

          {/* Zodiac Signs Grid */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-12">
            {zodiacSigns.map((sign) => (
              <Card
                key={sign.id}
                onClick={() => setSelectedSign(sign.id)}
                className={`p-4 text-center cursor-pointer transition-all duration-300 ${
                  selectedSign === sign.id 
                    ? 'card-glow bg-primary/20 border-primary' 
                    : 'hover:bg-card/80'
                }`}
              >
                <div className="text-4xl mb-2">{sign.emoji}</div>
                <h3 className="font-bold">{sign.name}</h3>
                <p className="text-xs text-muted-foreground">{sign.dates}</p>
              </Card>
            ))}
          </div>

          {/* Horoscope Content */}
          <Card key={selectedSign} className="p-8 gradient-celestial">
            <div className="mb-6 text-center">
              <h2 className="text-3xl font-bold mb-2 flex items-center justify-center gap-3">
                <span className="text-4xl">
                  {zodiacSigns.find(s => s.id === selectedSign)?.emoji}
                </span>
                {zodiacSigns.find(s => s.id === selectedSign)?.name}
              </h2>
              <p className="text-muted-foreground">
                {zodiacSigns.find(s => s.id === selectedSign)?.dates}
              </p>
            </div>

            <div className="w-full">
              <h3 className="text-xl font-bold mb-6 text-center">Horóscopo de Hoy</h3>
              
              <div className="space-y-6">
                <div>
                  <h3 className="text-xl font-bold mb-3 text-primary">💖 Amor</h3>
                  <p className="text-muted-foreground leading-relaxed">
                    {horoscopeData[selectedSign]?.amor}
                  </p>
                </div>
                
                <div>
                  <h3 className="text-xl font-bold mb-3 text-secondary">💼 Trabajo</h3>
                  <p className="text-muted-foreground leading-relaxed">
                    {horoscopeData[selectedSign]?.trabajo}
                  </p>
                </div>
                
                <div>
                  <h3 className="text-xl font-bold mb-3 text-accent">✨ Salud</h3>
                  <p className="text-muted-foreground leading-relaxed">
                    {horoscopeData[selectedSign]?.salud}
                  </p>
                </div>

                <div>
                  <h3 className="text-xl font-bold mb-3 text-accent">💰 Dinero</h3>
                  <p className="text-muted-foreground leading-relaxed">
                    {horoscopeData[selectedSign]?.dinero}
                  </p>
                </div>
              </div>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
};

const zodiacSigns = [
  { id: "aries", name: "Aries", emoji: "♈", dates: "Mar 21 - Abr 19" },
  { id: "tauro", name: "Tauro", emoji: "♉", dates: "Abr 20 - May 20" },
  { id: "geminis", name: "Géminis", emoji: "♊", dates: "May 21 - Jun 20" },
  { id: "cancer", name: "Cáncer", emoji: "♋", dates: "Jun 21 - Jul 22" },
  { id: "leo", name: "Leo", emoji: "♌", dates: "Jul 23 - Ago 22" },
  { id: "virgo", name: "Virgo", emoji: "♍", dates: "Ago 23 - Sep 22" },
  { id: "libra", name: "Libra", emoji: "♎", dates: "Sep 23 - Oct 22" },
  { id: "escorpio", name: "Escorpio", emoji: "♏", dates: "Oct 23 - Nov 21" },
  { id: "sagitario", name: "Sagitario", emoji: "♐", dates: "Nov 22 - Dic 21" },
  { id: "capricornio", name: "Capricornio", emoji: "♑", dates: "Dic 22 - Ene 19" },
  { id: "acuario", name: "Acuario", emoji: "♒", dates: "Ene 20 - Feb 18" },
  { id: "piscis", name: "Piscis", emoji: "♓", dates: "Feb 19 - Mar 20" },
];

const horoscopeData: Record<string, {
  amor: string;
  trabajo: string;
  salud: string;
  dinero: string;
}> = {
  aries: {
    amor: "Marte te impulsa a la acción y la conquista. Es un día para tomar la iniciativa. Si estás en pareja, reaviva la pasión con una propuesta audaz.",
    trabajo: "Lidera con determinación y coraje. Es el momento ideal para empezar ese proyecto ambicioso. Planificar es clave para que tu impulso tenga un impacto duradero.",
    salud: "Siente el fuego interior. Descarga tu energía con ejercicios intensos. Canaliza tu estrés con movimiento y no descuides tu descanso para mantener el equilibrio.",
    dinero: "Tu valentía te impulsa a asumir riesgos financieros calculados. Es un buen día para negociar o pedir un aumento. Actúa rápido, pero evita gastos impulsivos."
  },
  tauro: {
    amor: "La influencia de Venus te envuelve en sensualidad y estabilidad. Disfruta de los pequeños placeres con tu pareja; una cena exquisita fortalecerá el vínculo. Valora la lealtad.",
    trabajo: "La persistencia es tu mayor activo hoy. Los asuntos de dinero o inversión se ven favorecidos. Avanza con calma y método, asegurando cada paso con tu sentido práctico.",
    salud: "Concéntrate en el bienestar físico a través de la comodidad. Disfruta de un masaje o de alimentos nutritivos. La conexión con la naturaleza te aportará paz.",
    dinero: "La estabilidad y la paciencia rinden frutos. Concéntrate en asegurar tus bienes y revisar tus inversiones a largo plazo. Busca la calidad en tus adquisiciones."
  },
  geminis: {
    amor: "Tu mente está ágil, haciendo de hoy un día excelente para la comunicación. Las conversaciones ligeras y el flirteo están a la orden del día. Una salida social avivará la chispa.",
    trabajo: "Las oportunidades llegan a través de contactos y nuevas ideas. Es un día perfecto para reuniones o presentaciones. Enfoca tu curiosidad en una tarea a la vez para no dispersarte.",
    salud: "Necesitas estimulación mental. Leer, escribir o resolver acertijos mantendrá tu mente en forma. Asegúrate de respirar profundamente y no dejar que la ansiedad te abrume.",
    dinero: "El intercambio de información puede abrir nuevas fuentes de ingresos. Aprovecha tus habilidades de comunicación para buscar oportunidades de negocio. Evita dispersar demasiado tus fondos."
  },
  cancer: {
    amor: "La Luna exalta tu sensibilidad e instinto protector. Es un día para fomentar la intimidad emocional y la seguridad en tu hogar. Tu capacidad de nutrir será muy apreciada.",
    trabajo: "Confía en tu intuición al manejar asuntos profesionales. Las tareas relacionadas con el hogar o el cuidado se ven favorecidas. Busca la estabilidad en tu entorno laboral.",
    salud: "El descanso emocional es primordial. Evita las situaciones estresantes y dedica tiempo a actividades que te hagan sentir seguro/a. Cuida tu estómago.",
    dinero: "Los asuntos relacionados con el hogar o la familia pueden tener implicaciones financieras positivas. Es un buen momento para invertir en bienes raíces. Tu instinto te guiará hacia la seguridad económica."
  },
  leo: {
    amor: "Tu carisma brilla con luz propia. Es un día excelente para expresar tus sentimientos de forma grandiosa. Haz un gesto espectacular que demuestre tu afecto incondicional.",
    trabajo: "Es el momento de asumir el liderazgo y mostrar tu talento. Los proyectos creativos están destinados al éxito. Confía en tu visión y sé generoso/a con tus compañeros.",
    salud: "Tu vitalidad es alta. Disfruta de actividades al aire libre o deportes que te permitan brillar. Mantente erguido/a y con una actitud positiva.",
    dinero: "Tu confianza atrae la abundancia. Es un excelente día para pedir un reconocimiento o para lanzar un proyecto propio. No temas invertir en tu imagen personal."
  },
  virgo: {
    amor: "La atención al detalle mejora tu relación. Pequeños actos de servicio pueden ser muy valiosos. Si buscas amor, presta atención a la rutina, allí puede estar.",
    trabajo: "La precisión y la organización te llevarán al éxito. Es un día ideal para pulir documentos o abordar tareas que requieran un análisis meticuloso. Tu ética laboral es tu mejor carta.",
    salud: "Establece una rutina de bienestar práctica y realista. Revisa tu dieta y tus hábitos diarios. El yoga o la meditación te ayudarán a calmar tu mente analítica.",
    dinero: "La organización de tus finanzas es prioritaria. Revisa tu presupuesto, elimina gastos innecesarios y planifica con detalle tus ahorros. La eficiencia se traduce directamente en ganancias."
  },
  libra: {
    amor: "Venus asegura armonía y encanto en tus relaciones. Es un día para el romance, la diplomacia y buscar el equilibrio. Si hay un conflicto, tu habilidad para mediar llevará a la paz.",
    trabajo: "Las asociaciones y colaboraciones son claves hoy. Busca un socio o trabaja en equipo para obtener mejores resultados. Tu sentido de la justicia favorece proyectos de diseño o legales.",
    salud: "Enfócate en el equilibrio interno y externo. Las actividades que combinan belleza y movimiento, como la danza, serán restauradoras. Cuida tu zona lumbar.",
    dinero: "Las asociaciones y los acuerdos traen beneficios económicos. Si tienes que firmar un contrato, hazlo hoy. Busca el equilibrio en tus cuentas."
  },
  escorpio: {
    amor: "Se revela una profunda intensidad en tus lazos afectivos. Es un día para explorar la pasión y la verdad sin miedo. Busca la transformación positiva en tu vida amorosa.",
    trabajo: "Tu poder de concentración es máximo. Es el momento perfecto para investigar o negociar tratos importantes. Tu visión estratégica te dará la ventaja.",
    salud: "Una limpieza o desintoxicación puede ser beneficiosa. Libera el estrés a través de ejercicios que te permitan sacar tu frustración. Busca la regeneración.",
    dinero: "Los recursos compartidos están en foco. Tu capacidad de investigación te ayudará a destapar información financiera oculta. Es un día para la transformación de tus activos."
  },
  sagitario: {
    amor: "Tu espíritu aventurero inspira a tu pareja. Planifica un viaje o una nueva experiencia. El amor se expande a través del humor y la sinceridad.",
    trabajo: "Tu optimismo y visión a largo plazo son contagiantes. Es un excelente día para la educación, las publicaciones o los negocios internacionales. No te detengas ante las limitaciones.",
    salud: "Busca la libertad y la expansión a través del movimiento. Deportes al aire libre o una caminata te darán la perspectiva que necesitas. La risa es tu mejor medicina.",
    dinero: "La expansión es la palabra clave. Las inversiones en educación, viajes o negocios con el extranjero se ven muy favorecidas. Sigue tu intuición audaz."
  },
  capricornio: {
    amor: "La responsabilidad y la madurez definen tus relaciones hoy. Demuestra tu amor a través de acciones concretas y el compromiso a largo plazo.",
    trabajo: "Tu disciplina te coloca a la cabeza. Es un día productivo para planificar tu carrera o consolidar tu reputación. La paciencia y el esfuerzo metódico cosecharán recompensas.",
    salud: "La estructura en tu rutina de ejercicios es clave. Dedica tiempo a fortalecer tu esqueleto. Sé constante y realista con tus metas.",
    dinero: "La disciplina y la ambición te aseguran el progreso profesional. Concéntrate en establecer estructuras sólidas para tus ingresos a largo plazo. Los esfuerzos pasados dan frutos."
  },
  acuario: {
    amor: "Buscas libertad e intelectualidad en el amor. Sorprende a tu pareja con ideas originales. Valora la amistad y el espacio mutuo.",
    trabajo: "Tu originalidad es tu superpoder. Propón ideas innovadoras en un ambiente de grupo. Es un día ideal para la tecnología. Rompe el molde.",
    salud: "La mente abierta te beneficiará. Experimenta con nuevas formas de ejercicio. La conexión con tu círculo social también es vital para tu bienestar emocional.",
    dinero: "Las ganancias pueden llegar de fuentes inesperadas o a través de tecnología. Un proyecto grupal o una idea original pueden revolucionar tu panorama financiero."
  },
  piscis: {
    amor: "La sensibilidad e imaginación tiñen tu vida amorosa. Es un día para la conexión espiritual y la empatía profunda. Sueña despierto/a con tu pareja.",
    trabajo: "Tu creatividad e intuición son tus mejores guías. Las artes o cualquier trabajo que requiera sensibilidad están muy favorecidos. Confía en tus corazonadas.",
    salud: "Busca la paz interior a través de la meditación, la música o el arte. Protege tus pies y busca momentos de soledad reparadora para recargar tus energías.",
    dinero: "Tu intuición es tu mejor guía financiera hoy, especialmente en áreas creativas. Ten cuidado de no ser demasiado generoso/a y establece límites claros en tus gastos."
  }
};

export default Horoscope;
